import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { legacyStationsApi } from '@/api/legacy';
import type { ClearPortsDto, UnassignStationDto, LinuxServer, WaitingStation, AssignStationDto, AllErrorsResponse, UnassignedStation } from '@/api/legacy';
import { useClientContext } from '@/features/client/ClientContext';

/**
 * Get stations for a specific client (sobid) - brief info
 */
export function useClientStations(sobid: number | undefined) {
  return useQuery({
    queryKey: ['legacy', 'stations', sobid],
    queryFn: () => legacyStationsApi.getByClient(sobid!),
    enabled: !!sobid && sobid > 0,
    staleTime: 60_000,
  });
}

/**
 * Get detailed station info (reports, logs, tasks)
 */
export function useStationDetails(sobid: number | undefined, stationName: string | null) {
  return useQuery({
    queryKey: ['legacy', 'stationDetails', sobid, stationName],
    queryFn: () => legacyStationsApi.getDetails(sobid!, stationName!),
    enabled: !!sobid && sobid > 0 && !!stationName,
    staleTime: 30_000,
  });
}

/**
 * Clear/refresh port scan for a station
 */
export function useClearPorts(sobid: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ClearPortsDto) => legacyStationsApi.clearPorts(sobid!, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'stations', sobid],
      });
      toast.success('Запрос на сканирование портов отправлен');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка обновления портов';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Unassign station from client
 */
export function useUnassignStation(sobid: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UnassignStationDto) =>
      legacyStationsApi.unassignStation(sobid!, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'stations', sobid],
      });
      toast.success('Станция отвязана');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка отвязки станции';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Get Linux servers and netmap info for a client
 */
export function useClientCard(sobid: number | undefined) {
  return useQuery<LinuxServer[]>({
    queryKey: ['legacy', 'clientCard', sobid],
    queryFn: () => legacyStationsApi.getClientCard(sobid!),
    enabled: !!sobid && sobid > 0,
    staleTime: 60_000,
  });
}

/**
 * Get waiting stations (not assigned to any client)
 */
export function useWaitingStations(enabled = true) {
  return useQuery<WaitingStation[]>({
    queryKey: ['legacy', 'waitingStations'],
    queryFn: () => legacyStationsApi.getWaitingStations(),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Assign a waiting station to a client
 */
export function useAssignStation(sobid: number | undefined) {
  const queryClient = useQueryClient();
  const { detail } = useClientContext();

  return useMutation({
    mutationFn: (dto: AssignStationDto) =>
      legacyStationsApi.assignStation(sobid!, detail?.client?.name || '', dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'stations', sobid],
      });
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'waitingStations'],
      });
      toast.success('Станция привязана');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка привязки станции';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Get all stations with errors across all available clients (Контроль РК)
 */
export function useAllStationsErrors() {
  return useQuery<AllErrorsResponse>({
    queryKey: ['legacy', 'allErrors'],
    queryFn: () => legacyStationsApi.getAllErrors(),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Get unassigned (orphaned) stations for the current user
 */
export function useUnassignedStations() {
  return useQuery<UnassignedStation[]>({
    queryKey: ['legacy', 'unassignedStations'],
    queryFn: () => legacyStationsApi.getUnassigned(),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Delete orphaned station (permanent deletion)
 */
export function useDeleteOrphanedStation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: { stationId: string; comment?: string }) =>
      legacyStationsApi.unassignStation(0, { ...dto, permanent: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'unassignedStations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'allErrors'],
      });
      toast.success('Станция удалена');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка удаления станции';
      toast.error('Ошибка', { description: message });
    },
  });
}
